import { useEffect, useRef, useState } from 'react';
import { keepPreviousData } from '@tanstack/react-query';
import { leseGemerkt, schreibeGemerkt } from '../lib/gemerkt';
import { useDebouncedValue } from '@mantine/hooks';
import { useNavigate, useSearch } from '@tanstack/react-router';
import {
  Alert,
  Anchor,
  Badge,
  Button,
  Group,
  Select,
  Switch,
  Table,
  Tabs,
  Text,
  TextInput,
} from '@mantine/core';
import { useDocumentsControllerList } from '@werkbank/shared';
import {
  DOC_STATUS_LABELS,
  DOC_TYPE_LABELS,
  formatCents,
  formatDateDE,
  invoiceKindSuffix,
  selectOptions,
} from '../lib/format';
import { LISTE_ALLE, TablePager, useSeiten } from '../components/TablePager';
import { StatusBadge } from '../components/StatusBadge';
import { AvatarChip } from '../components/AvatarChip';
import { EmptyState } from '../components/EmptyState';
import { ListPage } from '../components/ListPage';
import { listState } from '../components/listPageContract';
import { listMeta } from '../components/listPageContract';
import { TableCard } from '../components/TableCard';

/**
 * P1-E §4: Belegart-Tabs über der Filterzeile. Aufmaße und Mahnungen sind
 * KEINE Documents (eigene Tabellen MeasurementSheet/DunningNotice, nicht im
 * Journal) — dafür gibt es bewusst keine Tabs. Das Journal bleibt die
 * vollständige GoBD-Zweitsicht; nichts wird ausgeblendet.
 */
const TYPE_TABS: { value: string; label: string }[] = [
  { value: 'ALL', label: 'Alle' },
  { value: 'QUOTE', label: 'Angebote' },
  { value: 'INVOICE', label: 'Rechnungen' },
  { value: 'CREDIT_NOTE', label: 'Gutschriften' },
  { value: 'DELIVERY_NOTE', label: 'Lieferscheine' },
  { value: 'WORK_REPORT', label: 'Arbeitsnachweise' },
  { value: 'ORDER_CONF', label: 'Auftragsbestätigungen' },
];

/** Filter des Belegjournals, wie sie in der URL stehen (router: validateSearch). */
interface JournalSearch {
  customerId?: string;
  type?: string;
  status?: string;
  q?: string;
  draft?: boolean;
}

/** Tobias 2026-10-04: Hinweise am Arbeitsnachweis im Belegjournal. */
const LN_HINWEIS: Record<
  'MATERIAL' | 'FOLGETERMIN' | 'FREIGABE_OFFEN' | 'ABRECHNUNG',
  { text: string; farbe: string }
> = {
  MATERIAL: { text: 'Fertig – Material muss bestellt werden', farbe: 'red' },
  FOLGETERMIN: { text: 'Fertig – Folgetermin vereinbaren', farbe: 'yellow' },
  FREIGABE_OFFEN: { text: 'Fertig gemeldet · Freigabe offen', farbe: 'orange' },
  ABRECHNUNG: { text: 'Fertig für Abrechnung', farbe: 'green' },
};

export function DocumentsPage() {
  // Kundenakte-Chip springt mit ?customerId=…&type=… hierher (Faruq 2026-08-17).
  // Tobias 2026-09-10: Belegart, Status, „nur Entwürfe" und Suche leben in der
  // URL (replace, kein Verlaufs-Eintrag je Klick) — nach dem Sprung in einen
  // Beleg stellt „Zurück" im Browser genau diese Filter wieder her.
  const routeSearch = useSearch({ strict: false }) as JournalSearch;
  const navigate = useNavigate();
  const setzeFilter = (patch: Partial<JournalSearch>) =>
    void navigate({
      to: '/documents',
      search: (prev: JournalSearch) => {
        const next: JournalSearch = { ...prev, ...patch };
        for (const k of Object.keys(next) as (keyof JournalSearch)[]) {
          if (next[k] === undefined || next[k] === null || next[k] === '') delete next[k];
        }
        return next;
      },
      replace: true,
    });
  const type = routeSearch.type ?? null;
  const setType = (t: string | null) => setzeFilter({ type: t ?? undefined });
  const status = routeSearch.status ?? null;
  const setStatus = (st: string | null) => setzeFilter({ status: st ?? undefined });
  const draftOnly = routeSearch.draft === true;
  const setDraftOnly = (on: boolean) => setzeFilter({ draft: on ? true : undefined });
  // Tobias 2026-09-25: auch über die Sidebar oder aus einer anderen Seite
  // zurück ins Journal bleiben die Filter — ohne Filter in der URL stellt das
  // Journal die zuletzt benutzten wieder her (Gedächtnis des Browser-Tabs).
  const ohneFilter = (Object.values(routeSearch) as unknown[]).every((v) => v === undefined);
  const [gemerkt] = useState<JournalSearch>(() =>
    ohneFilter ? leseGemerkt<JournalSearch>('belege.journal', {}) : {},
  );
  useEffect(() => {
    if (Object.keys(gemerkt).length > 0) {
      void navigate({ to: '/documents', search: gemerkt, replace: true });
    }
  }, []);
  const ersterLauf = useRef(true);
  useEffect(() => {
    const erster = ersterLauf.current;
    ersterLauf.current = false;
    // Beim ersten Lauf läuft ggf. noch die Wiederherstellung — nicht überschreiben.
    if (erster && Object.keys(gemerkt).length > 0) return;
    schreibeGemerkt('belege.journal', routeSearch);
  }, [JSON.stringify(routeSearch)]);
  // Suche: lokal tippen, entprellt in die URL schreiben.
  const [search, setSearch] = useState(routeSearch.q ?? gemerkt.q ?? '');
  const customerFilter = routeSearch.customerId ?? null;

  // Vollanalyse S5 (M19-Muster): Suche debounced statt ein Request pro
  // Tastendruck; placeholderData hält die Tabelle beim Tippen stehen.
  const [debouncedSearch] = useDebouncedValue(search.trim(), 250);
  useEffect(() => {
    if (debouncedSearch !== (routeSearch.q ?? '')) setzeFilter({ q: debouncedSearch || undefined });
  }, [debouncedSearch]);
  const documents = useDocumentsControllerList(
    {
      search: debouncedSearch || undefined,
      type: (type as never) ?? undefined,
      status: (status as never) ?? undefined,
      customerId: customerFilter ?? undefined,
      take: LISTE_ALLE,
      draftOnly: draftOnly ? 'true' : undefined,
    },
    { query: { placeholderData: keepPreviousData } },
  );
  // Tobias 2026-09-09: Fertigmeldungen aus der App, die auf die Freigabe warten —
  // deutlicher Indikator oben und an der Zeile, damit nichts liegen bleibt.
  const pruefungOffen = (documents.data ?? []).filter((d) => d.reviewPending).length;
  // Kundenname für das Aktiv-Filter-Chip aus der ersten Belegzeile.
  const filterCustomerName = customerFilter
    ? (documents.data?.find((d) => d.customerId === customerFilter)?.customerName ?? 'Kunde')
    : null;

  // D-W1 QS-3: der Zähler steht nur noch im Kartenfuß; die Kopf-Meta trägt
  // Beschreibung und aktive Filter (Vollanalyse S6: gemeinsamer listMeta-Helfer).
  const meta = listMeta(
    !!documents.data,
    'Vollständiges Belegjournal des Betriebs',
    type ? DOC_TYPE_LABELS[type] : null,
    draftOnly ? 'nur Entwürfe' : status ? DOC_STATUS_LABELS[status] : null,
    search ? `Suche „${search}“` : null,
  );

  // D-W1 Welle 2 (A2-13, W2-B4): Seite auf das ListPage-Gerüst migriert —
  // die vier Zustände (Laden/Fehler/Leer/Gefüllt) trägt das Gerüst.
  const seiten = useSeiten(documents.data, 'belege');
  return (
    <ListPage
      title="Belegjournal"
      meta={meta}
      tabs={
        /* Belegart-Tabs (P1-E §4): der einzige Belegart-Filter (D-W1 BE-1/BE-2). */
        <Tabs
          value={type ?? 'ALL'}
          onChange={(v) => setType(v === 'ALL' ? null : v)}
          keepMounted={false}
          data-testid="documents-type-tabs"
        >
          <Tabs.List>
            {TYPE_TABS.map((t) => (
              <Tabs.Tab key={t.value} value={t.value} data-testid={`documents-tab-${t.value}`}>
                {t.label}
              </Tabs.Tab>
            ))}
          </Tabs.List>
        </Tabs>
      }
      filters={
        <>
          <TextInput
            placeholder="Suche (Nummer, Kunde)…"
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            w={280}
          />
          <Select
            placeholder="Status"
            clearable
            disabled={draftOnly}
            data={selectOptions(DOC_STATUS_LABELS)}
            value={status}
            onChange={setStatus}
            w={180}
          />
          <Switch
            label="nur Entwürfe"
            checked={draftOnly}
            onChange={(e) => setDraftOnly(e.currentTarget.checked)}
            data-testid="documents-draft-only"
          />
          {customerFilter && (
            <Button
              variant="light"
              size="xs"
              rightSection="×"
              onClick={() => setzeFilter({ customerId: undefined })}
              data-testid="documents-clear-customer"
            >
              Kunde: {filterCustomerName}
            </Button>
          )}
        </>
      }
      state={listState(documents, documents.data?.length === 0)}
      errorText="Belege konnten nicht geladen werden."
      empty={
        <TableCard testId="documents-card">
          <EmptyState text="Keine Belege gefunden. Belege entstehen am Auftrag." />
        </TableCard>
      }
    >
      {pruefungOffen > 0 && (
        <Alert color="orange" variant="light" mb="md" data-testid="documents-review-alert">
          <Group justify="space-between" wrap="wrap">
            <Text size="sm" fw={600}>
              {pruefungOffen === 1
                ? '1 Arbeitsnachweis wurde fertig gemeldet und wartet auf die Freigabe.'
                : `${pruefungOffen} Arbeitsnachweise wurden fertig gemeldet und warten auf die Freigabe.`}
            </Text>
            {!(type === 'WORK_REPORT' && draftOnly) && (
              <Button
                size="xs"
                color="orange"
                variant="filled"
                onClick={() => setzeFilter({ type: 'WORK_REPORT', draft: true })}
                data-testid="documents-review-filter"
              >
                Nur diese anzeigen
              </Button>
            )}
          </Group>
        </Alert>
      )}
      <TableCard
        testId="documents-card"
        footer={
          <TablePager p={seiten} einzahl="Beleg" mehrzahl="Belege" testId="documents-pager" />
        }
      >
        <Table highlightOnHover data-testid="documents-table">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Belegart</Table.Th>
              <Table.Th>Nummer</Table.Th>
              <Table.Th>Kunde</Table.Th>
              <Table.Th>Auftrag</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Belegdatum</Table.Th>
              <Table.Th style={{ textAlign: 'right' }}>Brutto</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {seiten.sichtbar.map((d) => (
              <Table.Tr
                key={d.id}
                className={d.reviewPending ? 'wb-row-review' : undefined}
                style={{ cursor: 'pointer' }}
                onClick={() =>
                  void navigate({ to: '/documents/$documentId', params: { documentId: d.id } })
                }
              >
                <Table.Td>
                  {DOC_TYPE_LABELS[d.type]}
                  {invoiceKindSuffix(d.invoiceKind)}
                </Table.Td>
                <Table.Td>
                  <Text
                    fw={500}
                    className={`wb-tnum${d.status === 'CANCELLED' ? ' wb-strike' : ''}`}
                  >
                    {d.number ?? '(Entwurf)'}
                  </Text>
                  {/* Sage-Uebernahme: die alte Nummer als Hinweis fuers Buero
                      (Faruq 2026-09) - auf dem gedruckten Beleg steht sie nicht. */}
                  {d.legacySageId && (
                    <Text size="xs" c="dimmed" className="wb-tnum" data-testid="doc-legacy-number">
                      Sage {d.legacySageId}
                    </Text>
                  )}
                </Table.Td>
                <Table.Td>
                  <Group gap="xs" wrap="nowrap">
                    <AvatarChip name={d.customerName} />
                    {d.customerName}
                  </Group>
                </Table.Td>
                <Table.Td>
                  {/* P1-E §4: Auftrags-Spalte (leer bei auftragslosen Belegen). */}
                  {d.orderId && d.orderNumber ? (
                    <Anchor
                      size="sm"
                      className="wb-tnum"
                      title={d.orderTitle ?? undefined}
                      onClick={(e) => {
                        e.stopPropagation();
                        void navigate({ to: '/orders/$orderId', params: { orderId: d.orderId! } });
                      }}
                    >
                      {d.orderNumber}
                    </Anchor>
                  ) : (
                    ''
                  )}
                </Table.Td>
                <Table.Td>
                  <Group gap={6} wrap="nowrap">
                    <StatusBadge status={d.status} />
                    {/* Tobias 2026-10-04: was als Nächstes zu tun ist (statt immer
                        „Fertig gemeldet · Freigabe offen"). */}
                    {d.lnHinweis ? (
                      <Badge
                        color={LN_HINWEIS[d.lnHinweis].farbe}
                        autoContrast
                        variant="filled"
                        size="sm"
                        data-testid="doc-review-badge"
                        data-hinweis={d.lnHinweis}
                      >
                        {LN_HINWEIS[d.lnHinweis].text}
                      </Badge>
                    ) : d.reviewPending ? (
                      <Badge
                        color="orange"
                        variant="filled"
                        size="sm"
                        data-testid="doc-review-badge"
                      >
                        Fertig gemeldet · Freigabe offen
                      </Badge>
                    ) : null}
                  </Group>
                </Table.Td>
                <Table.Td className="wb-tnum">{formatDateDE(d.issueDate)}</Table.Td>
                <Table.Td className="wb-tnum" style={{ textAlign: 'right' }}>
                  {formatCents(d.grossTotalCents)} €
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </TableCard>
    </ListPage>
  );
}
